import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { CommonService } from './common.service';

export interface PaginationState {
  pageNumber: number;
  pageSize: number;
}

export interface ExtendedPaginationState extends PaginationState {
  selectedChannel?: string; // For templates (SMS, WhatsApp, RCS, Email, etc.)
  tabType?: string; // For both templates (ALL/DRAFT) and campaigns (PUBLISHED/DRAFT)
  filters?: any; // Current applied filters
  sortField?: string; // Current sort field
  sortDirection?: number; // Current sort direction (1 for asc, -1 for desc)
}

@Injectable({
  providedIn: 'root',
})
export class PaginationStateService {
  private readonly storageKey = 'pagination_states';
  private readonly defaultPageNumber = 1; // Use 1-based page numbers for consistency with ngx-bootstrap
  private readonly defaultPageSize = 10;

  private currentMainSection: string | null = null;

  constructor(
    private router: Router,
    private commonService: CommonService,
  ) {
    this.initializeNavigationTracking();
  }

  /**
   * Save pagination state for a specific route
   */
  saveState(routePath: string, pageNumber: number, pageSize: number): void {
    try {
      const states = this.getAllStates();
      states[routePath] = { pageNumber, pageSize };
      const encodedData = this.commonService.encodeData(JSON.stringify(states));
      sessionStorage.setItem(this.storageKey, encodedData);
    } catch (error) {
      console.warn('Failed to save pagination state:', error);
    }
  }

  /**
   * Get pagination state for a specific route
   * Returns default values if not found or on error
   */
  getState(routePath: string): PaginationState {
    try {
      const states = this.getAllStates();
      const state = states[routePath];

      if (state && this.isValidState(state)) {
        return state;
      }
    } catch (error) {
      console.warn('Failed to get pagination state:', error);
    }

    // Return default values
    const defaultState = {
      pageNumber: this.defaultPageNumber,
      pageSize: this.defaultPageSize,
    };
    return defaultState;
  }

  /**
   * Save extended pagination state with channel/tab information and filters/sorting
   */
  saveExtendedState(
    routePath: string,
    pageNumber: number,
    pageSize: number,
    selectedChannel?: string,
    tabType?: string,
    additionalState?: {
      filters?: any;
      sortField?: string;
      sortDirection?: number;
    },
  ): void {
    try {
      const states = this.getAllExtendedStates();
      states[routePath] = {
        pageNumber,
        pageSize,
        selectedChannel,
        tabType,
        filters: additionalState?.filters,
        sortField: additionalState?.sortField,
        sortDirection: additionalState?.sortDirection,
      };
      const encodedData = this.commonService.encodeData(JSON.stringify(states));
      sessionStorage.setItem(this.storageKey, encodedData);
    } catch (error) {
      console.warn('Failed to save extended pagination state:', error);
    }
  }

  /**
   * Get extended pagination state for a specific route
   */
  getExtendedState(routePath: string): ExtendedPaginationState {
    try {
      const states = this.getAllExtendedStates();
      const state = states[routePath];

      if (state && this.isValidExtendedState(state)) {
        return state;
      }

      return {
        pageNumber: this.defaultPageNumber,
        pageSize: this.defaultPageSize,
        selectedChannel: undefined,
        tabType: undefined,
        filters: undefined,
        sortField: undefined,
        sortDirection: undefined,
      };
    } catch (error) {
      console.warn('Error getting extended pagination state:', error);
      return {
        pageNumber: this.defaultPageNumber,
        pageSize: this.defaultPageSize,
        selectedChannel: undefined,
        tabType: undefined,
        filters: undefined,
        sortField: undefined,
        sortDirection: undefined,
      };
    }
  }

  /**
   * Clear pagination state for a specific route
   */
  clearState(routePath: string): void {
    try {
      const states = this.getAllExtendedStates();
      delete states[routePath];
      const encodedData = this.commonService.encodeData(JSON.stringify(states));
      sessionStorage.setItem(this.storageKey, encodedData);
    } catch (error) {
      console.warn('Failed to clear pagination state:', error);
    }
  }

  /**
   * Clear all pagination states
   */
  clearAllStates(): void {
    try {
      sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      console.warn('Failed to clear all pagination states:', error);
    }
  }

  /**
   * Get all stored extended pagination states
   */
  private getAllExtendedStates(): Record<string, ExtendedPaginationState> {
    try {
      const stored = sessionStorage.getItem(this.storageKey);
      if (!stored) {
        return {};
      }

      // Decode the stored data first, then parse JSON
      const decodedData = this.commonService.decodeData(stored);
      if (!decodedData) {
        console.warn('PaginationStateService: Failed to decode stored data');
        return {};
      }

      return JSON.parse(decodedData);
    } catch (error) {
      console.warn(
        'PaginationStateService: Failed to retrieve/decode states, returning empty:',
        error,
      );
      return {};
    }
  }

  /**
   * Get all stored pagination states (backward compatibility)
   */
  private getAllStates(): Record<string, PaginationState> {
    return this.getAllExtendedStates();
  }

  /**
   * Validate extended pagination state structure
   */
  private isValidExtendedState(state: any): state is ExtendedPaginationState {
    return (
      state &&
      typeof state.pageNumber === 'number' &&
      typeof state.pageSize === 'number' &&
      state.pageNumber >= 1 && // 1-based page numbers
      state.pageSize >= 1 &&
      (state.selectedChannel === undefined ||
        typeof state.selectedChannel === 'string') &&
      (state.tabType === undefined || typeof state.tabType === 'string')
    );
  }

  /**
   * Validate pagination state structure (backward compatibility)
   */
  private isValidState(state: any): state is PaginationState {
    return this.isValidExtendedState(state);
  }

  /**
   * Initialize navigation tracking to clear states when moving to different sections
   */
  private initializeNavigationTracking(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.handleNavigation((event as NavigationEnd).url);
      });
  }

  /**
   * Handle navigation events and clear states when moving to different main sections
   */
  private handleNavigation(url: string): void {
    const newMainSection = this.extractMainSection(url);

    if (
      newMainSection &&
      this.currentMainSection &&
      newMainSection !== this.currentMainSection
    ) {
      this.clearAllStates();
    }

    this.currentMainSection = newMainSection;
  }

  /**
   * Extract the main section from a URL path
   */
  private extractMainSection(url: string): string | null {
    // Remove query parameters and get base path
    const basePath = url.split('?')[0];

    // Extract the first path segment after the root
    const pathSegments = basePath
      .split('/')
      .filter((segment) => segment.length > 0);

    if (pathSegments.length === 0) {
      return 'home'; // Root path
    }

    const firstSegment = pathSegments[0];

    // Map common routes to main sections
    const sectionMapping: { [key: string]: string } = {
      templates: 'templates',
      campaigns: 'campaigns',
      segments: 'segments',
      'campaign-scheduler': 'campaign-scheduler',
      'budget-management': 'budget-management',
      reports: 'reports',
      'shorten-url': 'shorten-url',
      configuration: 'configuration',
      form: 'form-designer',
      'form-designer': 'form-designer',
    };

    return sectionMapping[firstSegment] || firstSegment;
  }
}
